import { useMemo, useRef, useState } from 'react';

import type { SortingState } from '@tanstack/react-table';
import { FlagIcon, HandCoinsIcon, LightbulbIcon, UsersRoundIcon, WalletIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { PageSubHeader, PageSubHeaderBreadcrumbs } from '@/components/organisms/PageSubHeader';
import { ResponsiveSidebarPanel } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { buildFilterBadges } from '@/components/ui/custom/ComplexFilter/buildFilterBadges';
import { ComplexFilterBar } from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  ComplexFilterCombobox,
  FilterDefinition,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ColumnVisibilityMenu } from '@/components/ui/table/column-visibility-menu';
import { DataTable } from '@/components/ui/table/data-table';
import { HiddenColumnsIndicator } from '@/components/ui/table/hidden-columns-indicator';
import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import { useServerPagination } from '@/components/ui/table/useServerPagination';
import useDebouncedCallback from '@/hooks/useDebouncedCallback';
import { getFilterValues, useTableFilters } from '@/hooks/useTableFilters';
import { selectAccountId, selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import type { AccountDebt, DebtsResponse, DebtStatus, PtpStatus } from '@/shared/types/contacts';
import { PageName } from '@/shared/types/segment';
import { TABLE_LAYOUT_FILL_CLASSES, TABLE_PAGINATION } from '@/util/constants';

import { DEBT_SORT_FIELD, debtColumns, PTP_STATUS_KEY } from './debtColumns';
import { DebtDetailPanel } from '../components/DebtDetailPanel';
import { DEBT_STATUS_KEY } from '../constants';
import { useDebts } from '../hooks/useDebts';

const DEFAULT_SORT = '-created';
const SEARCH_DEBOUNCE_MS = 300;

const selectDebts = (page: DebtsResponse) => page?.debts ?? [];
const getDebtId = (debt: AccountDebt) => debt.debt_id;

export const DebtsPage = () => {
  useSegments(PageName.DEBTS);

  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const accountId = useSelector(selectAccountId);
  useTitle(t('pages.debts'));

  const [searchValue, setSearchValue] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSetSearch = useDebouncedCallback(setSearch, SEARCH_DEBOUNCE_MS, []);

  const [openPillId, setOpenPillId] = useState<string | null>(null);
  const [selectedDebtId, setSelectedDebtId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);
  const customizeButtonRef = useRef<HTMLButtonElement>(null);
  const { filters, activeFilterCount, setFilter, removeFilter, resetFilters } = useTableFilters({
    storageKey: `debtsFilters:${accountId}`,
  });

  const yesNoOptions = [
    { label: t('common.yes'), value: 'true' },
    { label: t('common.no'), value: 'false' },
  ];

  const filterDefinitions: FilterDefinition[] = [
    {
      id: 'status',
      label: t('common.status'),
      icon: LightbulbIcon,
      type: 'radio',
      options: (Object.keys(DEBT_STATUS_KEY) as DebtStatus[]).map((status) => ({
        label: t(DEBT_STATUS_KEY[status]),
        value: status,
      })),
    },
    {
      id: 'ptp_status',
      label: t('contacts.debts.ptp_status'),
      icon: HandCoinsIcon,
      type: 'radio',
      options: (Object.keys(PTP_STATUS_KEY) as PtpStatus[]).map((status) => ({
        label: t(PTP_STATUS_KEY[status]),
        value: status,
      })),
    },
    {
      id: 'has_flag',
      label: t('contacts.debts.has_flag'),
      icon: FlagIcon,
      type: 'radio',
      options: yesNoOptions,
    },
    {
      id: 'has_party',
      label: t('contacts.debts.has_party'),
      icon: UsersRoundIcon,
      type: 'radio',
      options: yesNoOptions,
    },
  ];

  const filterBadges = buildFilterBadges(filterDefinitions, filters, { setFilter, removeFilter });

  const [sortColumn] = sorting;
  const sortField = sortColumn && DEBT_SORT_FIELD[sortColumn.id];
  const sort = sortField ? `${sortColumn.desc ? '-' : ''}${sortField}` : DEFAULT_SORT;

  const query = {
    limit: TABLE_PAGINATION,
    sort,
    q: search || undefined,
    status: getFilterValues(filters, 'status')[0],
    ptp_status: getFilterValues(filters, 'ptp_status')[0],
    has_flag: getFilterValues(filters, 'has_flag')[0],
    has_party: getFilterValues(filters, 'has_party')[0],
  };

  const { pages, total, status, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useDebts(query);

  const {
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage: paginationHasNextPage,
    currentPageData,
  } = useServerPagination<DebtsResponse, AccountDebt>(pages, fetchNextPage, {
    dataSelector: selectDebts,
    resetDependencies: [search, filters, sort],
    externalIsFetching: isFetchingNextPage,
    serverHasNextPage: hasNextPage,
  });

  const columns = useMemo(() => debtColumns(slug), [slug]);
  const {
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    hideableColumns,
    hasHiddenColumns,
  } = useColumnVisibility(columns, 'debtsColumnVisibility');
  const selectedDebt = pages.flatMap(selectDebts).find((debt) => debt.debt_id === selectedDebtId);

  const emptyStateMessage =
    status === 'error' ? (
      <div className="flex flex-col items-center gap-3">
        <p className="text-sm text-muted-foreground">{t('common.something_wrong')}</p>
        <Button onClick={() => refetch()} size="sm" variant="outline-neutral">
          {t('common.reload')}
        </Button>
      </div>
    ) : (
      t('contacts.debts.no_debts_found')
    );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
    debouncedSetSearch(event.target.value.trim());
  };

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full w-full" autoSaveId="debts-detail">
      <ResizablePanel order={1} className="flex min-h-0 flex-col overflow-hidden">
        <PageSubHeader
          breadcrumbs={
            <PageSubHeaderBreadcrumbs>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      <div className="flex h-8 w-8 items-center justify-center">
                        <WalletIcon size={16} className="text-muted-foreground" />
                      </div>
                      <div className="mr-4 ml-2 h-4 w-px bg-border" />
                      <span className="text-sm leading-5 font-normal text-foreground">
                        {t('contacts.debts.title')}
                      </span>
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </PageSubHeaderBreadcrumbs>
          }
        />

        <PageContentWrapper noGutters>
          <div className={`${TABLE_LAYOUT_FILL_CLASSES} gap-4`}>
            <div className="flex flex-col gap-4 pl-1 sm:flex-row sm:items-center sm:justify-between">
              <SearchBar
                placeholder={t('contacts.debts.search_placeholder')}
                value={searchValue}
                onChange={handleSearchChange}
              />
              <div className="flex items-center gap-2">
                <ComplexFilterCombobox
                  filterDefinitions={filterDefinitions}
                  filters={filters}
                  setFilter={setFilter}
                  removeFilter={removeFilter}
                  activeFilterCount={activeFilterCount}
                  onOpenActivePill={setOpenPillId}
                  showBulkActions={false}
                />
                <ColumnVisibilityMenu
                  columns={hideableColumns}
                  columnVisibility={columnVisibility}
                  onColumnVisibilityChange={setColumnVisibility}
                  onReset={resetColumnVisibility}
                  open={isCustomizeColumnsOpen}
                  onOpenChange={setIsCustomizeColumnsOpen}
                  onInteractOutside={(e) => {
                    if (e.target === customizeButtonRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                />
              </div>
            </div>

            <ComplexFilterBar
              filters={filterBadges}
              openPillId={openPillId}
              onOpenPillChange={setOpenPillId}
              onClearAll={resetFilters}
              showBulkActions={false}
            />

            <DataTable.Root<AccountDebt, unknown>
              fill
              className="min-h-0 flex-1"
              columns={columns}
              data={currentPageData}
              pagination
              currentPageIndex={currentPageIndex}
              setCurrentPageIndex={setCurrentPageIndex}
              fetchNextPage={goToNextPage}
              hasNextPage={paginationHasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              rowCount={total}
              onRowClick={(debt) =>
                setSelectedDebtId((current) => (current === debt.debt_id ? null : debt.debt_id))
              }
              getRowId={getDebtId}
              activeRowId={selectedDebt?.debt_id}
              sorting={sorting}
              onSortingChange={setSorting}
              manualSorting
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              resourceName="debt"
              emptyStateMessage={emptyStateMessage}
              isLoading={status === 'pending'}
            >
              <DataTable.Content />
              <DataTable.Pagination
                centerContent={
                  hasHiddenColumns ? (
                    <HiddenColumnsIndicator
                      onCustomize={() => setIsCustomizeColumnsOpen((open) => !open)}
                      buttonRef={customizeButtonRef}
                    />
                  ) : undefined
                }
              />
            </DataTable.Root>
          </div>
        </PageContentWrapper>
      </ResizablePanel>

      {selectedDebt && (
        <ResponsiveSidebarPanel
          title={selectedDebt.debt_external_id || selectedDebt.debt_id}
          onClose={() => setSelectedDebtId(null)}
        >
          <DebtDetailPanel debt={selectedDebt} />
        </ResponsiveSidebarPanel>
      )}
    </ResizablePanelGroup>
  );
};
